// lib/leasing/campaignMembership.ts
//
// ONE answer to "is this shortlist row part of that campaign's results".
//
// THE DECISION THIS FILE CARRIES. A finished campaign is a receipt, not a live view: it keeps the
// brands it analysed, for good. That was not true until 2026-08-15, because membership was the
// single pointer `lastCampaignId` and a shortlist row is unique per (property, brand): re-sourcing
// the same asset re-pointed the row at the new run and the older, completed, already-paid-for
// campaign silently shrank. Measured on property cmldnhzv20001v9eko4qcpr5j, five brands moved.
//
// SO THE TWO COLUMNS MEAN DIFFERENT THINGS, and the split is the whole fix:
//
//   - `lastCampaignId` = the run that OWNS the row right now. Its score, its rationale, its
//     in-flight state. Every WRITE path stays keyed on it: settling PENDING rows, aborting a run,
//     deleting a campaign's rows. Those must only ever touch what the run currently owns.
//   - `campaignIds`   = every run that ever scored the row. Append-only, so it cannot lose a
//     member. Every READ that answers "what is on this shortlist" and every COUNT keys on it.
//
// Mixing them up costs a silent double-count in one direction and the original silent loss in the
// other, which is why both live behind these two functions rather than inline in thirty files.
//
// NOTHING HERE WRITES `campaignIds`. The append is a database trigger
// (prisma/manual/2026-08-15_shortlist_campaign_membership.sql) precisely so that no writer has to
// know it exists, including production, which still writes shortlist rows through the `Lead` view.

/**
 * Prisma `where` fragment for "the rows on this campaign's shortlist".
 *
 * Kept as a plain object literal rather than a `Prisma.LeadWhereInput`: client bundles import the
 * sibling predicate below, and dragging the generated Prisma types in here for one field would
 * make this module server-only for no gain.
 */
export const leadsOfCampaign = (campaignId: string): { campaignIds: { has: string } } => ({
  campaignIds: { has: campaignId },
});

/**
 * Same question against a lead the kernel already holds.
 *
 * Falls back to `lastCampaignId` when `campaignIds` is missing, which is not defensive padding:
 * the list endpoints send a trimmed lead DTO, and a row hydrated by an older payload still has to
 * answer for the run that owns it rather than silently drop off its own shortlist.
 */
export const leadBelongsToCampaign = (
  lead: { campaignIds?: readonly string[] | null; lastCampaignId?: string | null } | null | undefined,
  campaignId: string,
): boolean => {
  if (!lead || !campaignId) return false;
  if (Array.isArray(lead.campaignIds) && lead.campaignIds.length > 0) {
    return lead.campaignIds.includes(campaignId);
  }
  return lead.lastCampaignId === campaignId;
};
